<script setup lang="ts">
  import { markFirstAdRequest } from '../../utils/consent/firstAdRequest'
  import { computed, onMounted } from 'vue'
  import type { NtpAdEvent } from '../../composables/analytics/types'
  import type { Slot } from '../../types/assertive'
  import { getIndexOfPlacement } from '../../utils/ad-ops/adFunctions'
  import {
    destroyedAyInstanceCodes,
    refreshedAyInstanceCodes,
  } from '../../utils/ad-ops/ayInstanceCodes'
  import { whenConsentSettled } from '../../utils/consent/consentGate'
  import {
    markConsentTiming,
    measureConsentTiming,
  } from '../../utils/consent/consentTiming'

  interface AssertiveAdAttributes {
    'data-render-registered': boolean
  }
  export interface AssertiveProps {
    placement: string
    tilePosition?: number
    lazyRender?: boolean
    sticky?: boolean
    adHeight?: string
    adWidth?: string
    adInstanceId?: string
  }

  const props = withDefaults(defineProps<AssertiveProps>(), {
    tilePosition: -1,
    lazyRender: true,
    sticky: false,
    adHeight: 'auto',
    adWidth: 'auto',
    adInstanceId: '',
  })

  const { adClicked, renderWhenCurrentElementIsVisible } = useAdFunctions()
  const { $ayLoaded, $ayManagerEnv } = useNuxtApp()
  const placementEl = useTemplateRef<HTMLElement | null>('placementEl')
  const placementClassName = ref<string | null>(props.placement)

  const renderAYElement = ref<boolean>(false)
  const adUnitPath = ref<string>('')
  let instanceCode = ''
  let displayedIn: HTMLElement | null = null
  let unmounted = false
  const placementAttributes = computed<AssertiveAdAttributes>(() => {
    return {
      'data-render-registered': false,
    }
  })

  function getEventData(): NtpAdEvent {
    return {
      ntp_tile_position: props.tilePosition,
      ntp_ad_brand: 'programmatic',
      ntp_ad_provider: 'programmatic',
      ntp_creative_id: adUnitPath.value,
      ntp_placement_name: props.placement,
      ntp_ad_refresh: false,
      ntp_ay_instance_code: instanceCode,
    }
  }

  let adEngaged = false
  let hasClicked = false

  function onMouseOver() {
    adEngaged = true
  }

  function onMouseOut() {
    adEngaged = false
    hasClicked = false
  }

  function isFocusInsideAd() {
    return placementEl.value?.contains(document.activeElement) ?? false
  }

  function onLeaveEvent() {
    if (!adEngaged || hasClicked || !isFocusInsideAd()) return
    hasClicked = true
    adClicked(getEventData(), adUnitPath.value)
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') onLeaveEvent()
  }

  const renderAYAd = async () => {
    if (
      !placementEl.value ||
      placementEl.value?.getAttribute('data-render-registered') === 'true' ||
      renderAYElement.value
    )
      return
    renderAYElement.value = true
    placementEl.value?.setAttribute('data-render-registered', 'true')

    const index = getIndexOfPlacement(props.placement, placementEl.value)

    placementClassName.value =
      index > 0
        ? `${props.placement} ${props.placement}-${index}`
        : props.placement

    // Hold the ad request, not the markup, until the manager is up and consent
    // has settled. The package releases ordinary US traffic on its deadline,
    // while unresolved GDPR traffic remains restricted.
    markConsentTiming('first-visible-ad-wait-start', true)
    await Promise.all([$ayLoaded, whenConsentSettled()])
    markConsentTiming('first-visible-ad-ready', true)
    measureConsentTiming(
      'first-visible-ad-wait',
      'first-visible-ad-wait-start',
      'first-visible-ad-ready'
    )

    // The wait above is bounded but real, so the slot can be gone by the time
    // it resolves — an article click-through or a scroll in a virtualised feed
    // unmounts it mid-wait. Displaying into a detached node would define a slot
    // that never renders and bill it as an unfilled impression.
    const container = placementEl.value
    if (!container?.isConnected) return

    // Listen first so the slot this display defines is not missed. Display by
    // element: the placement name would target every copy on the page.
    $ayManagerEnv?.cmd.push(() => {
      if (!container.isConnected) return
      displayedIn = container
      $ayManagerEnv?.onEvent('afterDefineSlot', onAfterDefineSlot)
      markConsentTiming('first-visible-ad-display', true)
      markFirstAdRequest('display')
      $ayManagerEnv?.display(container)
    })
  }

  // Copies of a placement share its name, so claim the slot by containment.
  // Searched from the container because it may already be detached.
  function onAfterDefineSlot(code: string, slot: Slot) {
    if (instanceCode || !displayedIn) return
    const slotId = slot.getSlotElementId()
    const ownsSlot = Array.from(displayedIn.querySelectorAll('[id]')).some(
      (el) => el.id === slotId
    )
    if (!ownsSlot) return
    instanceCode = code
    adUnitPath.value = code
    // Defined after unmount, so nothing else will destroy it.
    if (unmounted) destroyInstance()
  }

  // Until AY defines the slot there is no code to destroy, so the listener
  // stays until it arrives.
  function destroyInstance() {
    $ayManagerEnv?.cmd.push(() => {
      const code = instanceCode
      if (!code) return
      refreshedAyInstanceCodes.delete(code)
      // The native fallback already destroyed it.
      if (!destroyedAyInstanceCodes.delete(code)) {
        $ayManagerEnv?.destroy([code], { useInstanceCodes: true })
      }
      $ayManagerEnv?.offEvent('afterDefineSlot', onAfterDefineSlot)
    })
  }

  onMounted(() => {
    placementEl.value?.addEventListener('mouseenter', onMouseOver, {
      passive: true,
    })
    placementEl.value?.addEventListener('mouseleave', onMouseOut, {
      passive: true,
    })
    window.addEventListener('blur', onLeaveEvent, { passive: true })
    window.addEventListener('pagehide', onLeaveEvent, { passive: true })
    window.addEventListener('beforeunload', onLeaveEvent, { passive: true })
    document.addEventListener('visibilitychange', onVisibilityChange, {
      passive: true,
    })

    // Watch for visibility now rather than after the manager loads, so a slot
    // that is only briefly in view while the feed lays out is not missed.
    renderWhenCurrentElementIsVisible(
      placementEl.value,
      props.lazyRender,
      props.sticky,
      renderAYAd
    )
  })

  onUnmounted(() => {
    placementEl.value?.removeEventListener('mouseenter', onMouseOver)
    placementEl.value?.removeEventListener('mouseleave', onMouseOut)
    window.removeEventListener('blur', onLeaveEvent)
    window.removeEventListener('pagehide', onLeaveEvent)
    window.removeEventListener('beforeunload', onLeaveEvent)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    unmounted = true
    if (renderAYElement.value) destroyInstance()
    renderAYElement.value = false
  })
</script>

<template>
  <div
    :id="props.placement"
    ref="placementEl"
    :class="placementClassName"
    :data-ay-manager-id="props.placement"
    v-bind="placementAttributes"
    :data-ad-instance-id="props.adInstanceId"
  />
</template>
